import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Users, 
  BookOpen, 
  MessageSquare, 
  LogOut,
  RefreshCw,
  Home,
  Menu,
  CalendarDays,
  Plus,
  Award,
  FileText,
  UserPlus,
  Briefcase,
  ClipboardCheck,
  Calendar,
  DollarSign,
  Heart,
  Clock,
  Megaphone,
  Mail,
  FileWarning,
  GraduationCap,
  BarChart3,
  Shield,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

import { AdminLogin } from "../components/admin/AdminLogin";
import { AdminSessionDialog } from "../components/admin/AdminSessionDialog";
import { AdminViewDialog } from "../components/admin/AdminViewDialog";
import { ReferralsTable, SignupsTable, SessionsTable, ContactsTable } from "../components/admin/AdminDataTables";
import { CertificatesPanel } from "../components/admin/CertificatesPanel";
import { AdminDocumentsPanel } from "../components/admin/AdminDocumentsPanel";
import { ParticipantsPanel } from "../components/admin/ParticipantsPanel";
import { CasesPanel } from "../components/admin/CasesPanel";
import { AttendancePanel } from "../components/admin/AttendancePanel";
import { EventsPanel } from "../components/admin/EventsPanel";
import { GrantsPanel } from "../components/admin/GrantsPanel";
import { DonorsPanel } from "../components/admin/DonorsPanel";
import { HoursPanel } from "../components/admin/HoursPanel";
import { CareersPanel } from "../components/admin/CareersPanel";
import { AnnouncementsPanel } from "../components/admin/AnnouncementsPanel";
import { EmailSettingsPanel } from "../components/admin/EmailSettingsPanel";
import { LAPReportsPanel } from "../components/admin/LAPReportsPanel";
import { ServicesPanel } from "../components/admin/ServicesPanel";
import { SecurityPanel } from "../components/admin/SecurityPanel";
import { AdminPublicFormsPanel } from "../components/admin/AdminPublicFormsPanel";
import { AdminServiceDeliveryPanel } from "../components/admin/AdminServiceDeliveryPanel";
import { useSessionTimeout } from "../hooks/useSessionTimeout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [activeTab, setActiveTab] = useState("referrals");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  const handleSessionTimeout = useCallback(() => {
    setIsAuthenticated(false);
    setCredentials({ username: "", password: "" });
    setShowTimeoutWarning(false);
    toast.error("Session expired due to inactivity. Please log in again.");
  }, []);

  const handleSessionWarning = useCallback(() => {
    setShowTimeoutWarning(true);
    toast.warning("Your session will expire in 2 minutes due to inactivity.");
  }, []);

  useSessionTimeout(handleSessionTimeout, handleSessionWarning, isAuthenticated);
  
  const [referrals, setReferrals] = useState([]);
  const [signups, setSignups] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  const [certificates, setCertificates] = useState([]);
  const [certPageEnabled, setCertPageEnabled] = useState(false);

  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [sessionForm, setSessionForm] = useState({
    title: "", start_date: "", end_date: "", day_of_week: "",
    time: "", location: "", max_capacity: 15, notes: ""
  });

  const [participants, setParticipants] = useState([]);

  const getAuthHeader = useCallback(() => ({
    auth: { username: credentials.username, password: credentials.password }
  }), [credentials.username, credentials.password]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    try {
      await axios.post(`${API}/admin/login`, {}, getAuthHeader());
      setIsAuthenticated(true);
      toast.success("Login successful");
    } catch {
      toast.error("Invalid credentials");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCredentials({ username: "", password: "" });
    toast.success("Logged out successfully");
  };

  const fetchData = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    const auth = getAuthHeader();
    try {
      const [referralsRes, signupsRes, contactsRes, sessionsRes, statsRes, certsRes, certSettingsRes, participantsRes] = await Promise.all([
        axios.get(`${API}/admin/referrals`, auth),
        axios.get(`${API}/admin/class-signups`, auth),
        axios.get(`${API}/admin/contacts`, auth),
        axios.get(`${API}/admin/class-sessions`, auth),
        axios.get(`${API}/admin/stats`, auth),
        axios.get(`${API}/admin/certificates`, auth),
        axios.get(`${API}/admin/certificate-settings`, auth),
        axios.get(`${API}/admin/participants`, auth),
      ]);
      setReferrals(referralsRes.data);
      setSignups(signupsRes.data);
      setContacts(contactsRes.data);
      setSessions(sessionsRes.data);
      setStats(statsRes.data);
      setCertificates(certsRes.data);
      setCertPageEnabled(certSettingsRes.data.enabled || false);
      setParticipants(participantsRes.data);
    } catch {
      toast.error("Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getAuthHeader]);

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [isAuthenticated, fetchData]);

  const updateStatus = async (type, id, newStatus) => {
    try {
      await axios.put(`${API}/admin/${type}/${id}/status`, { status: newStatus }, getAuthHeader());
      toast.success("Status updated");
      fetchData();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await axios.delete(`${API}/admin/${type}/${id}`, getAuthHeader());
      toast.success("Item deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete item");
    }
  };

  const openSessionDialog = (session = null) => {
    if (session) {
      setEditingSession(session);
      setSessionForm({
        title: session.title, start_date: session.start_date.split('T')[0],
        end_date: session.end_date.split('T')[0], day_of_week: session.day_of_week,
        time: session.time, location: session.location,
        max_capacity: session.max_capacity, notes: session.notes || ""
      });
    } else {
      setEditingSession(null);
      setSessionForm({ title: "", start_date: "", end_date: "", day_of_week: "", time: "", location: "", max_capacity: 15, notes: "" });
    }
    setSessionDialogOpen(true);
  };

  const handleSessionSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSession) {
        await axios.put(`${API}/admin/class-sessions/${editingSession.id}`, sessionForm, getAuthHeader());
        toast.success("Session updated");
      } else {
        await axios.post(`${API}/admin/class-sessions`, sessionForm, getAuthHeader());
        toast.success("Session created");
      }
      setSessionDialogOpen(false);
      fetchData();
    } catch {
      toast.error("Failed to save session");
    }
  };

  const handleViewItem = (item) => {
    setSelectedItem(item);
    setViewDialogOpen(true);
  };

  const handleToggleCertPage = async () => {
    try {
      const res = await axios.put(`${API}/admin/certificate-settings`, {}, getAuthHeader());
      setCertPageEnabled(res.data.enabled);
      toast.success(res.data.enabled ? "Certificate page enabled" : "Certificate page disabled");
    } catch {
      toast.error("Failed to update settings");
    }
  };

  const handleCreateCert = async (form) => {
    try {
      await axios.post(`${API}/admin/certificates`, form, getAuthHeader());
      toast.success("Certificate created successfully");
      fetchData();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create certificate");
      return false;
    }
  };

  const handleDeleteCert = async (id) => {
    if (!window.confirm("Are you sure you want to delete this certificate?")) return;
    try {
      await axios.delete(`${API}/admin/certificates/${id}`, getAuthHeader());
      toast.success("Certificate deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete certificate");
    }
  };

  if (!isAuthenticated) {
    return (
      <AdminLogin
        credentials={credentials}
        setCredentials={setCredentials}
        onLogin={handleLogin}
        isLoggingIn={isLoggingIn}
      />
    );
  }

  const tabs = [
    { id: "referrals", label: "Referrals", icon: Users, count: stats?.pending_referrals },
    { id: "lap-reports", label: "LAP Reports", icon: FileWarning },
    { id: "signups", label: "Class Signups", icon: BookOpen, count: stats?.pending_signups },
    { id: "sessions", label: "Class Schedule", icon: CalendarDays, count: stats?.upcoming_sessions },
    { id: "participants", label: "Participants", icon: UserPlus, count: stats?.active_participants },
    { id: "cases", label: "Cases", icon: Briefcase, count: stats?.open_cases },
    { id: "attendance", label: "Attendance", icon: ClipboardCheck },
    { id: "services", label: "Services", icon: GraduationCap },
    { id: "events", label: "Events", icon: Calendar, count: stats?.total_events },
    { id: "grants", label: "Grants", icon: DollarSign, count: stats?.awarded_grants },
    { id: "donors", label: "Donors", icon: Heart, count: stats?.total_donors },
    { id: "hours", label: "Hours & Users", icon: Clock, count: stats?.pending_hours },
    { id: "careers", label: "Careers", icon: Briefcase },
    { id: "announcements", label: "Announcements", icon: Megaphone },
    { id: "contacts", label: "Messages", icon: MessageSquare, count: stats?.unread_contacts },
    { id: "certificates", label: "Certificates", icon: Award, count: stats?.total_certificates },
    { id: "documents", label: "Documents", icon: FileText },
    { id: "email-settings", label: "Email Settings", icon: Mail },
    { id: "public-forms", label: "Training & Fair", icon: GraduationCap },
    { id: "service-delivery", label: "Case Tracking", icon: BarChart3 },
    { id: "security", label: "Security", icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform`}>
        <div className="flex flex-col h-full">
          <div className="p-6 pb-2">
            <div className="flex items-center gap-3 mb-4">
              <img src="/logo-new.png" alt="The Pillar Project Logo" className="h-12 w-auto object-contain rounded-lg" />
              <span className="font-semibold text-gray-900" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>Admin Panel</span>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto px-6 space-y-1 pb-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }}
                data-testid={`tab-${tab.id}`}
                className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl transition-colors text-sm ${
                  activeTab === tab.id ? "bg-warm-50 text-warm-700" : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <tab.icon className="w-4 h-4" />
                  <span className="font-medium">{tab.label}</span>
                </div>
                {tab.count > 0 && (
                  <span className="bg-warm-700 text-white text-xs px-2 py-0.5 rounded-full">{tab.count}</span>
                )}
              </button>
            ))}
          </nav>

          <div className="p-6 pt-2 border-t border-gray-100 space-y-2">
            <Link to="/"><Button variant="outline" className="w-full justify-start gap-2 rounded-xl text-sm"><Home className="w-4 h-4" />View Website</Button></Link>
            <Button variant="ghost" onClick={handleLogout} data-testid="admin-logout-btn" className="w-full justify-start gap-2 text-gray-600 hover:text-red-600 rounded-xl text-sm">
              <LogOut className="w-4 h-4" />Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button className="lg:hidden p-2 hover:bg-gray-100 rounded-lg" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-semibold text-gray-900" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
              {tabs.find(t => t.id === activeTab)?.label}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === "sessions" && (
              <Button onClick={() => openSessionDialog()} data-testid="add-session-btn" className="bg-warm-700 hover:bg-warm-800 text-white gap-2">
                <Plus className="w-4 h-4" />Add Session
              </Button>
            )}
            {["referrals", "signups", "sessions", "contacts"].includes(activeTab) && (
              <Button variant="outline" size="sm" onClick={fetchData} disabled={isLoading} data-testid="refresh-data-btn" className="gap-2">
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />Refresh
              </Button>
            )}
          </div>
        </header>

        {stats && (
          <div className="p-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            <div className="bg-white rounded-xl p-4 border border-gray-100" data-testid="stat-referrals">
              <p className="text-sm text-gray-600">Referrals</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_referrals}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100" data-testid="stat-participants">
              <p className="text-sm text-gray-600">Participants</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_participants}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100" data-testid="stat-cases">
              <p className="text-sm text-gray-600">Open Cases</p>
              <p className="text-2xl font-bold text-gray-900">{stats.open_cases}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100" data-testid="stat-signups">
              <p className="text-sm text-gray-600">Signups</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_signups}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100" data-testid="stat-users">
              <p className="text-sm text-gray-600">Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_users}</p>
            </div>
          </div>
        )}

        <div className="p-6">
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            {activeTab === "referrals" && <ReferralsTable referrals={referrals} onView={handleViewItem} onUpdateStatus={updateStatus} onDelete={deleteItem} />}
            {activeTab === "lap-reports" && (
              <div className="p-6"><LAPReportsPanel getAuthHeader={getAuthHeader} /></div>
            )}
            {activeTab === "signups" && <SignupsTable signups={signups} onView={handleViewItem} onUpdateStatus={updateStatus} onDelete={deleteItem} />}
            {activeTab === "sessions" && <SessionsTable sessions={sessions} onEdit={openSessionDialog} onUpdateStatus={updateStatus} onDelete={deleteItem} />}
            {activeTab === "participants" && (
              <div className="p-6"><ParticipantsPanel getAuthHeader={getAuthHeader} onRefresh={fetchData} /></div>
            )}
            {activeTab === "cases" && (
              <div className="p-6"><CasesPanel getAuthHeader={getAuthHeader} participants={participants} onRefresh={fetchData} /></div>
            )}
            {activeTab === "attendance" && (
              <div className="p-6"><AttendancePanel getAuthHeader={getAuthHeader} sessions={sessions} participants={participants} /></div>
            )}
            {activeTab === "services" && (
              <div className="p-6"><ServicesPanel getAuthHeader={getAuthHeader} /></div>
            )}
            {activeTab === "events" && (
              <div className="p-6"><EventsPanel getAuthHeader={getAuthHeader} onRefresh={fetchData} /></div>
            )}
            {activeTab === "grants" && (
              <div className="p-6"><GrantsPanel getAuthHeader={getAuthHeader} /></div>
            )}
            {activeTab === "donors" && (
              <div className="p-6"><DonorsPanel getAuthHeader={getAuthHeader} /></div>
            )}
            {activeTab === "hours" && (
              <div className="p-6"><HoursPanel getAuthHeader={getAuthHeader} /></div>
            )}
            {activeTab === "careers" && (
              <div className="p-6"><CareersPanel getAuthHeader={getAuthHeader} onRefresh={fetchData} /></div>
            )}
            {activeTab === "announcements" && (
              <div className="p-6"><AnnouncementsPanel getAuthHeader={getAuthHeader} /></div>
            )}
            {activeTab === "contacts" && <ContactsTable contacts={contacts} onView={handleViewItem} onUpdateStatus={updateStatus} onDelete={deleteItem} />}
            {activeTab === "certificates" && (
              <div className="p-6">
                <CertificatesPanel
                  certificates={certificates}
                  certPageEnabled={certPageEnabled}
                  onTogglePage={handleToggleCertPage}
                  onCreateCert={handleCreateCert}
                  onDeleteCert={handleDeleteCert}
                  onRefresh={fetchData}
                  isLoading={isLoading}
                  getAuthHeader={getAuthHeader}
                />
              </div>
            )}
            {activeTab === "documents" && (
              <div className="p-6">
                <AdminDocumentsPanel getAuthHeader={getAuthHeader} />
              </div>
            )}
            {activeTab === "email-settings" && (
              <div className="p-6">
                <EmailSettingsPanel getAuthHeader={getAuthHeader} />
              </div>
            )}
            {activeTab === "public-forms" && (
              <div className="p-6">
                <AdminPublicFormsPanel getAuthHeader={getAuthHeader} />
              </div>
            )}
            {activeTab === "service-delivery" && (
              <div className="p-6">
                <AdminServiceDeliveryPanel getAuthHeader={getAuthHeader} />
              </div>
            )}
            {activeTab === "security" && (
              <div className="p-6">
                <SecurityPanel getAuthHeader={getAuthHeader} />
              </div>
            )}
          </div>
        </div>
      </main>

      <AdminViewDialog open={viewDialogOpen} onOpenChange={setViewDialogOpen} item={selectedItem} />
      <AdminSessionDialog
        open={sessionDialogOpen}
        onOpenChange={setSessionDialogOpen}
        editingSession={editingSession}
        sessionForm={sessionForm}
        setSessionForm={setSessionForm}
        onSubmit={handleSessionSubmit}
      />
    </div>
  );
};

export default AdminDashboard;
